// app/src/dashboard/widgets/chart/chartColors.ts
//
// Single source of truth for chart colors. Reads the active theme's CSS
// variables (defined per-theme in client/Main.css) at runtime so every chart
// adapts automatically to the current theme — including new palettes added
// later (Tokyo Night, …). No widget should hardcode hex colors anymore.
//
// The theme classes are toggled on <body> (see client/hooks/useColorMode.ts),
// so we resolve variables against document.body, falling back to
// documentElement for safety (SSR / very early paint).

/** Read a CSS custom property and wrap it as an hsl() color. */
function hslVar(name: string, fallback = ""): string {
  if (typeof window === "undefined") return fallback;
  const root = window.document.body ?? window.document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue(name).trim();
  return raw ? `hsl(${raw})` : fallback;
}

/** Same as hslVar but with an explicit alpha (0–1). */
function hslVarAlpha(name: string, alpha: number, fallback = ""): string {
  if (typeof window === "undefined") return fallback;
  const root = window.document.body ?? window.document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue(name).trim();
  return raw ? `hsl(${raw} / ${alpha})` : fallback;
}

/**
 * Theme-aware chart color set. Call inside the component render (or chart
 * option builder) so it re-reads on theme change. Pass the current `isDark`
 * from useDashboardData() only for the rare hardcoded-fallback cases.
 */
export function chartColors() {
  return {
    // P&L semantics — identical to the rest of the app (calendar, KPIs).
    positive: hslVar("--edgely-pnl-positive", "#22c55e"),
    negative: hslVar("--edgely-pnl-negative", "#ef4444"),

    // Brand accent + its tints for multi-series charts (never a rainbow).
    accent: hslVar("--primary", "#00c9a7"),
    series: [
      hslVar("--chart-series-1", "#00c9a7"),
      hslVar("--chart-series-2", "#3d6eff"),
      hslVar("--chart-series-3", "#7aa2f7"),
    ],

    // Structure.
    grid: hslVar("--chart-grid", "#1e3a52"),
    axis: hslVar("--chart-axis", "#4a7fa5"),
    neutral: hslVar("--chart-neutral", "#64748b"),

    // Candlestick (TradingView convention up/down), themed.
    candleUp: hslVar("--chart-candle-up", "#26a69a"),
    candleDown: hslVar("--chart-candle-down", "#ef5350"),

    // Trade entry markers, themed per palette. Chosen for MAXIMUM hue separation
    // between long and short, and to avoid clashing with the green/red candles:
    //   Buy  (long entry)  = secondary (purple in Tokyo Night) — cool
    //   Sell (short entry) = warning   (amber/gold)            — warm
    // Per-theme --chart-marker-* tokens override these if defined.
    // Exit markers (single-trade views only) reuse these same buy/sell colors:
    // the exit is the opposite side of the trade, so it takes the opposite color.
    markerBuy: hslVar("--chart-marker-buy") || hslVar("--secondary", "#a78bfa"),
    markerSell: hslVar("--chart-marker-sell") || hslVar("--warning", "#f5c842"),
    chartBg: hslVar("--chart-canvas-bg", "transparent"),
    chartText: hslVar("--chart-axis", "#d1d5db"),
    chartBorder: hslVar("--chart-grid", "#374151"),
  };
}

/** P&L line/fill color helper: pass a value, get the right token color. */
export function pnlColor(value: number): string {
  return value >= 0
    ? hslVar("--edgely-pnl-positive", "#22c55e")
    : hslVar("--edgely-pnl-negative", "#ef4444");
}

/** P&L color with alpha (for gradient fills). */
export function pnlColorAlpha(value: number, alpha: number): string {
  return value >= 0
    ? hslVarAlpha("--edgely-pnl-positive", alpha, "rgba(34,197,94,0.2)")
    : hslVarAlpha("--edgely-pnl-negative", alpha, "rgba(239,68,68,0.2)");
}

/**
 * Tiered win-rate color (good / neutral / bad), token-based. Replaces the
 * duplicated winRateColor() helpers that hardcoded #00C9A7 / #F5C842 / #FF5252.
 */
export function winRateColor(winRate: number): string {
  if (winRate >= 60) return hslVar("--edgely-pnl-positive", "#22c55e");
  if (winRate >= 45) return hslVar("--warning", "#f5c842");
  return hslVar("--edgely-pnl-negative", "#ef4444");
}
